/** Parent row for the management {@link DataTable}. */
import * as React from 'react';
import { User, CalendarDays, Clock, Users } from 'lucide-react';
import { Button } from '@/components/forms/Button';
import { LabelBadge } from '@/components/common/LabelBadge';
import { TableRow } from '@/components/common/data-table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  getParentBusinessStatus,
  PARENT_PENDING_STATUS_CONFIG,
  PARENT_ACTIVE_STATUS_CONFIG,
  PARENT_REMOTE_MILESTONES,
  getParentMilestoneStatus,
  type ParentTabView,
} from './parent-configs';
import type { ParentRecord } from '@/types/parent.types';
import {
  canShowArchiveRow,
  canShowReactivateRow,
  type ParentRowPageActions,
  formatParentDate,
  deriveParentContact,
} from './parent-table-utils';

/** Props for {@link ParentRow}. */
interface Props {
  record: ParentRecord;
  childCount?: number;
  childList?: Array<{ id: string; firstName: string; lastName: string }>;
  onView?: (id: string) => void;
  onEdit?: (id: string, record: ParentRecord) => void;
  onInviteChild?: (id: string, record: ParentRecord) => void;
  onViewStudents?: (id: string, record: ParentRecord) => void;
  pageActions?: ParentRowPageActions;
  activeTab: ParentTabView;
  onArchive?: (userId: string) => void;
  onReactivate?: (userId: string) => void;
}

/** Parent row for the management {@link DataTable}. */
export const ParentRow: React.FC<Props> = ({
  record,
  childCount = 0,
  childList = [],
  onView,
  onEdit,
  onInviteChild,
  onViewStudents,
  pageActions,
  activeTab,
  onArchive,
  onReactivate,
}) => {
  const { tab, status } = getParentBusinessStatus({
    onboardingStatus: record.onboardingStatus,
    folderSituation: record.folderSituation,
    user: record.user,
  });
  const userStatus = record.user?.status;
  const showArchive = canShowArchiveRow(userStatus, pageActions?.canArchiveRow);
  const showReactivate = canShowReactivateRow(userStatus, activeTab, pageActions?.canReactivateRow);

  function handleArchive(): void {
    onArchive?.(record.userId);
  }

  function handleReactivate(): void {
    onReactivate?.(record.userId);
  }

  const statusConfig =
    tab === 'pre_registered'
      ? PARENT_PENDING_STATUS_CONFIG[status as keyof typeof PARENT_PENDING_STATUS_CONFIG]
      : PARENT_ACTIVE_STATUS_CONFIG[status as keyof typeof PARENT_ACTIVE_STATUS_CONFIG];

  const StatusIcon = statusConfig?.icon ?? Clock;
  const completionPct = record.completion.completedSectionsPercentage;

  const primaryContact = deriveParentContact(
    record.sections ?? {},
    record.user?.phone,
    record.user?.email,
    record.user?.primaryContactKind,
  );

  return (
    <TableRow>
      <td className="!px-3">
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-[color-mix(in_srgb,var(--color-1-nuance-1)_10%,transparent)]">
            <User className="w-4 h-4 text-[var(--color-1-nuance-1)]" />
          </div>
          <div>
            <p className="text-sm font-medium text-foreground leading-tight">
              {record.user?.firstName} {record.user?.lastName}
            </p>
          </div>
        </div>
      </td>

      <td className="!px-3">
        <div className="flex flex-col gap-0.5">
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-1 min-w-0">
                  <primaryContact.icon
                    className="w-4 h-4 shrink-0"
                    style={{ color: 'var(--color-1-nuance-1)' }}
                  />
                  <span className="text-sm truncate max-w-[160px] cursor-default">
                    {primaryContact.value}
                  </span>
                </div>
              </TooltipTrigger>
              {primaryContact.value !== 'N/R' && (
                <TooltipContent>
                  <p>{primaryContact.value}</p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
          {primaryContact.secondaryValue && (
            <span className="text-xs text-muted-foreground">{primaryContact.secondaryValue}</span>
          )}
        </div>
      </td>

      <td className="!px-3">
        {childCount > 0 && childList.length > 0 ? (
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-1.5 cursor-default">
                  <Users className="w-3.5 h-3.5 text-[var(--color-1-nuance-1)] shrink-0" />
                  <span className="text-sm">{childCount}</span>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <div className="space-y-0.5">
                  {childList.map((c) => (
                    <p key={c.id}>
                      {c.firstName} {c.lastName}
                    </p>
                  ))}
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <div className="flex items-center gap-1">
            <Users className="w-3.5 h-3.5 text-[var(--color-1-nuance-1)] shrink-0" />
            <span className="text-sm">{childCount}</span>
          </div>
        )}
      </td>

      <td className="!px-3">
        <div className="flex items-center gap-2">
          <div className="w-20 h-1.5 bg-[var(--color-1-nuance-3)] rounded-full overflow-hidden">
            <div
              className="h-full bg-[var(--color-2-nuance-1)] rounded-full transition-all"
              style={{ width: `${completionPct}%` }}
            />
          </div>
          <span className="text-xs text-muted-foreground tabular-nums">{completionPct}%</span>
        </div>
      </td>

      <td className="!px-3">
        <div className="flex items-center gap-1.5">
          {PARENT_REMOTE_MILESTONES.map((m) => {
            const mStatus = getParentMilestoneStatus(record.sections, m.key);
            const Icon = m.icon;
            return (
              <div key={m.key} className="flex flex-col items-center gap-0.5" title={m.label}>
                <Icon
                  className="w-4 h-4"
                  style={{ color: mStatus === 'validated' ? m.validatedColor : m.pendingColor }}
                />
                <span
                  className="text-[10px]"
                  style={{ color: mStatus === 'validated' ? m.validatedColor : m.pendingColor }}
                >
                  {m.label}
                </span>
              </div>
            );
          })}
        </div>
      </td>

      <td className="!px-3">
        <div className="flex items-center gap-1">
          <CalendarDays className="w-3.5 h-3.5 text-[var(--color-1-nuance-1)] shrink-0" />
          <span className="text-sm">{formatParentDate(record.createdAt)}</span>
        </div>
      </td>

      <td className="!px-3">
        <LabelBadge
          label={statusConfig?.label ?? record.onboardingStatus}
          icon={StatusIcon}
          variant={statusConfig?.badgeVariant ?? 'outlined'}
          color={statusConfig?.badgeColor}
          size="md"
        />
      </td>

      <td className="text-center !px-3">
        <div className="flex items-center justify-center gap-1">
          <Button preset="row-view" onClick={() => onView?.(record.id)} />
          {pageActions?.canEditRow && (
            <Button preset="row-edit" onClick={() => onEdit?.(record.userId, record)} />
          )}
          {pageActions?.canInviteChild && (
            <Button preset="row-invite-child" onClick={() => onInviteChild?.(record.id, record)} />
          )}
          {pageActions?.canViewStudents && (
            <Button
              preset="row-view-students"
              onClick={() => onViewStudents?.(record.id, record)}
            />
          )}
          {showArchive ? <Button preset="row-archive" onClick={handleArchive} /> : null}
          {showReactivate ? <Button preset="row-reactivate" onClick={handleReactivate} /> : null}
        </div>
      </td>
    </TableRow>
  );
};
